import { MediaPicker } from '@/components/media/MediaPicker';
import type { MediaRow } from '@/routes/media/types';
import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import { type EventInput, type EventRow, fromLocalDateTime, toLocalDateTime } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/events`.
 *
 * docs/01-INFORMATION-ARCHITECTURE.md §6 splits the public page into upcoming
 * and past, so this list opens on what is coming and offers the other half as a
 * toggle. Paging back through a year of events to fix last autumn's conference
 * is the thing that makes an editor stop using the module.
 */
export function EventsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams();

  const columns: Column<EventRow>[] = [
    {
      key: 'title',
      label: t('content.columns.title'),
      render: (row) => (
        <div className="min-w-48">
          <span className="font-semibold text-primary">{valueFor(row.title, locale)}</span>

          {row.is_featured ? (
            <Chip tone="navy" className="mt-1">
              {t('content.events.featured')}
            </Chip>
          ) : null}
        </div>
      ),
    },
    {
      key: 'location',
      label: t('content.events.columns.location'),
      render: (row) => (
        <span className="text-secondary">{valueFor(row.location, locale) || '—'}</span>
      ),
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'translations',
      label: t('news.columns.translations'),
      render: (row) => (
        <span className="text-caption uppercase text-secondary">
          {row.translations_available.join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'starts_at',
      label: t('content.events.columns.starts'),
      sortable: true,
      align: 'end',
      render: (row) => (
        <time dateTime={row.starts_at} className="whitespace-nowrap text-secondary">
          {new Date(row.starts_at).toLocaleString()}
        </time>
      ),
    },
  ];

  return (
    <ResourceListScreen
      resource="events"
      title={t('nav.items.events')}
      intro={t('content.events.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.title, locale)}
      emptyTitle={t('content.events.empty.title')}
      emptyBody={t('content.events.empty.body')}
      newLabel={t('content.events.new')}
      deleteTitle={t('content.events.delete.title')}
      deleteBody={(label) => t('content.delete.body', { label })}
      filters={
        <Select
          label={t('content.events.when')}
          hideLabel
          value={String(list.params.past ?? '')}
          onChange={(event) => list.setFilter('past', event.target.value || null)}
          options={[
            { value: '', label: t('content.events.allTime') },
            { value: '0', label: t('content.events.upcoming') },
            { value: '1', label: t('content.events.past') },
          ]}
        />
      }
    />
  );
}

interface Fields {
  title: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  location: TranslatedValue;
  starts_at: string;
  ends_at: string;
  cover_media_id: number | null;
  cover: MediaRow | null;
  category: string;
  registration_url: string;
  is_featured: boolean;
  video_url: string;
  video_poster_media_id: number | null;
  video_poster: MediaRow | null;
  status: EventRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();

  const mayPublish = can('events.publish');

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<EventRow, Fields, EventInput>({
      resource: 'events',
      recordId,
      empty,
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'title',
        'slug',
        'description',
        'location',
        'starts_at',
        'ends_at',
        'cover_media_id',
        'category',
        'registration_url',
        'video_url',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('content.events.new') : t('content.events.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/events')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('content.fields.title')}
            name="title"
            value={values.title}
            onChange={(next) => setValue('title', next, { shouldDirty: true })}
            error={formState.errors.title?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record?.slug}
            published={values.status === 'published'}
            error={formState.errors.slug?.message}
          />

          <RichTextField
            label={t('content.fields.description')}
            value={values.description}
            onChange={(next) => setValue('description', next, { shouldDirty: true })}
            error={formState.errors.description?.message}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              {...register('starts_at')}
              type="datetime-local"
              label={t('content.events.fields.starts')}
              error={formState.errors.starts_at?.message}
            />

            <Input
              {...register('ends_at')}
              type="datetime-local"
              label={t('content.events.fields.ends')}
              hint={t('content.events.fields.endsHint')}
              error={formState.errors.ends_at?.message}
            />
          </div>

          <TranslatableField
            label={t('content.events.fields.location')}
            hint={t('content.events.fields.locationHint')}
            name="location"
            value={values.location}
            onChange={(next) => setValue('location', next, { shouldDirty: true })}
            required={false}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              {...register('category')}
              label={t('content.columns.category')}
              hint={t('content.events.fields.categoryHint')}
              error={formState.errors.category?.message}
            />

            <Input
              {...register('registration_url')}
              type="url"
              label={t('content.events.fields.registration')}
              error={formState.errors.registration_url?.message}
            />

            <MediaPicker
              label={t('content.fields.cover')}
              hint={t('content.fields.coverHint')}
              value={values.cover}
              onChange={(mediaId, media) => {
                setValue('cover_media_id', mediaId, { shouldDirty: true });
                setValue('cover', media, { shouldDirty: true });
              }}
              error={formState.errors.cover_media_id?.message}
            />
          </div>

          {/*
            §4 — the home page's rector-video card reads the *featured* event
            and falls back to a setting. So this block is not decoration: it
            decides what the front page plays.
          */}
          <fieldset className="space-y-4 rounded-card border border-subtle p-4">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('content.events.videoTitle')}
            </legend>

            <p className="text-caption text-muted">{t('content.events.videoHint')}</p>

            <label className="flex items-center gap-2 text-body-sm text-primary">
              <input
                type="checkbox"
                {...register('is_featured')}
                className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
              />
              {t('content.events.fields.featured')}
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                {...register('video_url')}
                type="url"
                label={t('content.events.fields.videoUrl')}
                error={formState.errors.video_url?.message}
              />

              <MediaPicker
                label={t('content.events.fields.videoPoster')}
                hint={t('content.fields.coverHint')}
                value={values.video_poster}
                onChange={(mediaId, media) => {
                  setValue('video_poster_media_id', mediaId, { shouldDirty: true });
                  setValue('video_poster', media, { shouldDirty: true });
                }}
                error={formState.errors.video_poster_media_id?.message}
              />
            </div>
          </fieldset>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="events"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              title: { label: t('content.fields.title'), value: values.title },
              description: { label: t('content.fields.description'), value: values.description },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    title: {},
    slug: {},
    description: {},
    location: {},
    starts_at: '',
    ends_at: '',
    cover_media_id: null,
    cover: null,
    category: '',
    registration_url: '',
    is_featured: false,
    video_url: '',
    video_poster_media_id: null,
    video_poster: null,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: EventRow): Fields {
  return {
    title: row.title,
    slug: row.slug,
    description: row.description,
    location: row.location,
    starts_at: toLocalDateTime(row.starts_at),
    ends_at: toLocalDateTime(row.ends_at),
    cover_media_id: row.cover_media_id ?? null,
    cover: row.cover ?? null,
    category: row.category ?? '',
    registration_url: row.registration_url ?? '',
    is_featured: row.is_featured,
    video_url: row.video_url ?? '',
    video_poster_media_id: row.video_poster_media_id ?? null,
    video_poster: row.video_poster ?? null,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): EventInput {
  return {
    title: form.title,
    slug: form.slug,
    description: form.description,
    location: form.location,
    // Required by the server; an empty box sends `''` and gets a 422 naming the
    // field, which is the right place for that message to land.
    starts_at: fromLocalDateTime(form.starts_at) ?? '',
    ends_at: fromLocalDateTime(form.ends_at),
    cover_media_id: form.cover_media_id,
    category: form.category.trim() === '' ? null : form.category.trim(),
    registration_url: form.registration_url.trim() === '' ? null : form.registration_url.trim(),
    is_featured: form.is_featured,
    video_url: form.video_url.trim() === '' ? null : form.video_url.trim(),
    video_poster_media_id: form.video_poster_media_id,
    status: form.status,
    published_at: form.published_at,
    pending_review: mayPublish ? form.pending_review : true,
  };
}
